import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription,
  CardFooter,
  CardHeader, 
  CardTitle
} from "@/components/ui/card";
import { Plus, Loader2 } from "lucide-react";
import { documentFormApi, formStepApi } from "@/services/api";
import FormStepComponent from "./FormStep";
import { DocumentForm } from "@/models";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";

interface FormBuilderProps {
  documentId: number;
  documentName?: string;
  preventFormSubmission?: boolean;
}

const FormBuilder = ({ documentId, documentName, preventFormSubmission = false }: FormBuilderProps) => {
  const { toast } = useToast();
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [stepTitle, setStepTitle] = useState("");
  const [nextStepLabel, setNextStepLabel] = useState("");
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const { 
    data: forms = [], 
    isLoading,
    refetch: refetchForms
  } = useQuery({
    queryKey: ['documentForms', { document_id: documentId }],
    queryFn: () => documentFormApi.list({ queryKey: ['documentForms', { document_id: documentId }] }),
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading form",
          description: error.message || "Failed to fetch document form",
        });
      }
    }
  });

  const currentForm = forms.length > 0 ? forms[0] as DocumentForm : null;
  
  // Ensure form steps are sorted by step_order when rendering
  const sortedSteps = currentForm?.form_steps ? 
    [...currentForm.form_steps].sort((a, b) => a.step_order - b.step_order) : 
    [];

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, type } = result;
    
    if (!destination || destination.index === source.index) return;
    
    if (type === 'steps' && sortedSteps.length > 0) {
      try {
        setIsReordering(true);
        
        // Use sortedSteps instead of currentForm.form_steps for reordering
        const reorderedSteps = Array.from(sortedSteps);
        const [movedStep] = reorderedSteps.splice(source.index, 1);
        reorderedSteps.splice(destination.index, 0, movedStep);
        
        // Update step orders manually since reorder doesn't exist
        for (let i = 0; i < reorderedSteps.length; i++) {
          await formStepApi.update(reorderedSteps[i].id, { step_order: i });
        }
        
        toast({
          title: "Steps reordered",
          description: "The steps have been reordered successfully",
        });
        
        await refetchForms();
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "Error reordering steps",
          description: error.message || "An error occurred while reordering the steps",
        });
      } finally {
        setIsReordering(false);
      }
    }
  };

  const handleCreateForm = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      setIsCreatingForm(true);
      await documentFormApi.create({
        document_id: documentId
      });
      
      toast({
        title: "Form created",
        description: "The form has been created successfully.",
      });
      
      await refetchForms();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error creating form",
        description: error.message || "An error occurred while creating the form",
      });
    } finally {
      setIsCreatingForm(false);
    }
  };

  const handleAddStep = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!currentForm) return;
    
    try {
      setIsAddingStep(true);
      await formStepApi.create({
        document_form_id: currentForm.id,
        step_order: currentForm.form_steps?.length || 0,
        step_title: stepTitle || null,
        next_step_label: nextStepLabel || "Next"
      });
      
      toast({
        title: "Step added",
        description: "The step has been added successfully.",
      });
      
      setStepTitle("");
      setNextStepLabel("");
      setStepDialogOpen(false);
      await refetchForms();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error adding step",
        description: error.message || "An error occurred while adding the step",
      });
    } finally {
      setIsAddingStep(false);
    }
  };

  const openStepDialog = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Opening add step dialog");
    setStepDialogOpen(true);
  };

  const handleFormUpdate = async (): Promise<void> => {
    await refetchForms();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (currentForm) {
    return (
      <div className="space-y-6" data-form-builder="true" >
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">Form Builder</h2>
          <Button 
            type="button" 
            onClick={openStepDialog}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Step
          </Button>
        </div>
        
        <div className="space-y-6">
          {sortedSteps && sortedSteps.length > 0 ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="form-steps" type="steps" direction="vertical">
                {(provided) => (
                  <div 
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-6"
                  >
                    {sortedSteps.map((step, index) => {
                      const draggableId = `step-${step.id}`;
                      return (
                        <Draggable 
                          key={draggableId}
                          draggableId={draggableId}
                          index={index}
                          isDragDisabled={isReordering}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`${snapshot.isDragging ? 'shadow-lg opacity-70 rounded-lg border-2 border-primary' : ''} transition-all`}
                              style={provided.draggableProps.style}
                            >
                              <FormStepComponent 
                                key={`step-component-${step.id}`}
                                form={currentForm}
                                step={step}
                                onFormUpdated={handleFormUpdate}
                                preventFormSubmission={preventFormSubmission}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <Card className="border-dashed">
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground mb-4">No steps defined yet. Add your first step to start building the form.</p>
                <Button 
                  type="button"
                  onClick={openStepDialog}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add First Step
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        <Dialog 
          open={stepDialogOpen} 
          onOpenChange={(open) => {
            if (!open && !isAddingStep) {
              setStepDialogOpen(false);
              setStepTitle("");
              setNextStepLabel("");
            }
          }}
        >
          <DialogContent 
            className="sm:max-w-[425px]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onPointerDownOutside={(e) => {
              if (isAddingStep) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader onClick={(e) => e.stopPropagation()}>
              <DialogTitle>Add Step</DialogTitle>
              <DialogDescription>
                You can leave the title blank to use the default (Step N).
                Next button label is optional and defaults to "Next".
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4" onClick={(e) => e.stopPropagation()}>
              <div className="grid grid-cols-4 items-center gap-4" onClick={(e) => e.stopPropagation()}>
                <label htmlFor="step-title" className="text-right col-span-1 text-xs font-medium">
                  Title
                </label>
                <Input
                  id="step-title"
                  placeholder={`Step ${(currentForm.form_steps?.length || 0) + 1}`}
                  value={stepTitle}
                  onChange={(e) => setStepTitle(e.target.value)}
                  className="col-span-3"
                  disabled={isAddingStep}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4" onClick={(e) => e.stopPropagation()}>
                <label htmlFor="next-step-label" className="text-right col-span-1 text-xs font-medium">
                  Next label
                </label>
                <Input
                  id="next-step-label"
                  placeholder="Next"
                  value={nextStepLabel}
                  onChange={(e) => setNextStepLabel(e.target.value)}
                  className="col-span-3"
                  disabled={isAddingStep}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <DialogFooter onClick={(e) => e.stopPropagation()}>
              <Button 
                type="button"
                variant="outline" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setStepDialogOpen(false);
                  setStepTitle("");
                  setNextStepLabel("");
                }} 
                disabled={isAddingStep}
              >
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleAddStep} 
                disabled={isAddingStep}
              >
                {isAddingStep ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Step
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-form-builder="true" onClick={(e) => e.stopPropagation()}>
      <Card className="border-dashed border-2">
        <CardHeader>
          <CardTitle>Create Form</CardTitle>
          <CardDescription>
            Create a form for {documentName ? `"${documentName}"` : "this document"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Click the button below to create a form. You can then add steps, sections, and fields to build your form.
          </p>
        </CardContent>
        <CardFooter>
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCreateForm(e);
            }} 
            disabled={isCreatingForm}
          >
            {isCreatingForm ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Create Form
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default FormBuilder;
